import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getDominanceHistory, getDominanceSummary } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const COLORS = { btc: '#f7931a', eth: '#627eea', usdt: '#26a17b', otherStable: '#2775ca', alt: '#8b5cf6' };

function fmtPct(n) {
  return n == null || !Number.isFinite(n) ? '—' : `${n.toFixed(2)}%`;
}
function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toFixed(0)}`;
}

// 100%-stacked (or absolute-value-stacked) area chart — each date is a
// column split into bands by `keys` (bottom to top), one invisible hit-rect
// per column carries a native <title> tooltip listing every band's value.
function StackedAreaChart({ rows, keys, colors, labels, valueFmt, height = 240 }) {
  if (rows.length === 0) return null;
  const W = 900, padL = 44, padR = 6, padT = 8, padB = 20;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const totals = rows.map((r) => keys.reduce((s, k) => s + (r[k] || 0), 0));
  const maxTotal = Math.max(1, ...totals);
  const colW = plotW / rows.length;

  // Precompute cumulative band boundaries (bottom→top) per row, as fractions of maxTotal.
  const bands = rows.map((r) => {
    let acc = 0;
    return keys.map((k) => {
      const v = r[k] || 0;
      const y0 = acc, y1 = acc + v;
      acc = y1;
      return { key: k, v, y0, y1 };
    });
  });

  const yFor = (v) => padT + plotH - (v / maxTotal) * plotH;
  const xFor = (i) => padL + i * colW;

  // Build one smooth-ish polygon path per band by connecting midpoints of columns.
  const paths = keys.map((k, ki) => {
    const top = rows.map((_, i) => [xFor(i) + colW / 2, yFor(bands[i][ki].y1)]);
    const bottom = rows.map((_, i) => [xFor(i) + colW / 2, yFor(bands[i][ki].y0)]).reverse();
    const pts = [...top, ...bottom];
    const d = 'M' + pts.map((p) => p.join(',')).join(' L') + ' Z';
    return { key: k, d };
  });

  const labelEvery = Math.max(1, Math.ceil(rows.length / 8));

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[maxTotal, maxTotal / 2, 0].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yFor(v)} y2={yFor(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 6} y={yFor(v) + 3} fontSize={10} fill="var(--text-dim)" textAnchor="end">{valueFmt(v)}</text>
        </g>
      ))}
      {paths.map((p) => (
        <path key={p.key} d={p.d} fill={colors[p.key]} opacity={0.82} stroke={colors[p.key]} strokeWidth={0.5} />
      ))}
      {/* invisible per-column hit area carrying the combined tooltip */}
      {rows.map((r, i) => (
        <rect key={r.date} x={xFor(i)} y={padT} width={colW} height={plotH} fill="transparent">
          <title>
            {r.date}
            {keys.map((k) => `\n${labels[k]}: ${valueFmt(r[k] || 0)}`).join('')}
          </title>
        </rect>
      ))}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xFor(i) + colW / 2} y={height - 5} fontSize={10} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

// Single-series area-under-line chart. The y-axis is fitted to the data's
// min/max (not anchored at 0) so each category's own swings stay visible.
function LineAreaChart({ rows, color, valueFmt, height = 200, showDates = true }) {
  const pts = rows.map((r, i) => ({ i, date: r.date, v: r.v })).filter((p) => Number.isFinite(p.v));
  if (pts.length < 2) return null;
  const W = 900, padL = 52, padR = 6, padT = 8, padB = showDates ? 20 : 6;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const min = Math.min(...pts.map((p) => p.v)), max = Math.max(...pts.map((p) => p.v));
  const pad = (max - min) * 0.08 || max * 0.02 || 1;
  const lo = min - pad, hi = max + pad;
  const n = rows.length;
  const xFor = (i) => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yFor = (v) => padT + plotH - ((v - lo) / (hi - lo)) * plotH;
  const line = pts.map((p) => `${xFor(p.i)},${yFor(p.v)}`).join(' L');
  const area = `M${xFor(pts[0].i)},${padT + plotH} L${line} L${xFor(pts[pts.length - 1].i)},${padT + plotH} Z`;
  const labelEvery = Math.max(1, Math.ceil(n / 8));
  const colW = plotW / Math.max(1, n - 1);

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[hi, (hi + lo) / 2, lo].map((v, k) => (
        <g key={k}>
          <line x1={padL} x2={W - padR} y1={yFor(v)} y2={yFor(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 6} y={yFor(v) + 3} fontSize={10} fill="var(--text-dim)" textAnchor="end">{valueFmt(v)}</text>
        </g>
      ))}
      <path d={area} fill={color} opacity={0.15} />
      <path d={`M${line}`} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      {pts.map((p) => (
        <rect key={p.date} x={xFor(p.i) - colW / 2} y={padT} width={colW} height={plotH} fill="transparent">
          <title>{`${p.date}\n${valueFmt(p.v)}`}</title>
        </rect>
      ))}
      {showDates && rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xFor(i)} y={height - 5} fontSize={10} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

// Change over the shown window, first vs. last valid value.
function pctChange(rows) {
  const vals = rows.map((r) => r.v).filter(Number.isFinite);
  if (vals.length < 2 || !vals[0]) return null;
  return ((vals[vals.length - 1] - vals[0]) / vals[0]) * 100;
}

function ChangeTag({ rows }) {
  const c = pctChange(rows);
  if (c == null) return null;
  const color = c >= 0 ? '#10b981' : '#ef4444';
  return <span style={{ fontSize: 11, fontWeight: 700, color }}>{c >= 0 ? '+' : ''}{c.toFixed(2)}%</span>;
}

export default function DominancePage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    setReady(false);
    try {
      const [sumRes, histRes] = await Promise.all([
        getDominanceSummary(),
        getDominanceHistory(180),
      ]);
      setSummary(sumRes.latest || null);
      setHistory(histRes.docs || []);
    } catch { setSummary(null); setHistory([]); }
    finally { setReady(true); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 4 * 60 * 60 * 1000); return () => clearInterval(iv); }, [load]);

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  const pctRows = history.map((d) => ({ date: d.date, btc: d.btcPct, eth: d.ethPct, usdt: d.usdtPct, otherStable: d.otherStablecoinsPct, alt: d.altcoinsPct }));
  const capRows = history.map((d) => ({ date: d.date, btc: d.btcMarketCapUsd, eth: d.ethMarketCapUsd, usdt: d.usdtMarketCapUsd, otherStable: d.otherStablecoinsMarketCapUsd, alt: d.altcoinsMarketCapUsd }));
  const pctLabels = { btc: t('dominance.legendBtc'), eth: t('dominance.legendEth'), usdt: t('dominance.legendUsdt'), otherStable: t('dominance.legendOtherStable'), alt: t('dominance.legendAlt') };
  const totalRows = history.map((d) => ({ date: d.date, v: d.totalMarketCapUsd }));
  const categoryRows = (key) => capRows.map((r) => ({ date: r.date, v: r[key] }));

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <h2 style={s.title}>{t('dominance.title')}</h2>
        <p style={s.subtitle}>{t('dominance.subtitle')}</p>
      </div>

      {!ready ? (
        <div style={s.loading}>{t('dominance.loading')}</div>
      ) : !summary ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌐</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('dominance.noData')}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('dominance.noDataSub')}</div>
        </div>
      ) : (
        <>
          <div style={s.cards}>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('dominance.cardBtc')}</span>
              <span style={{ ...s.cardValue, color: COLORS.btc }}>{fmtPct(summary.btcPct)}</span>
              <span style={s.cardSub}>{fmtUsd(summary.btcMarketCapUsd)}</span>
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('dominance.cardEth')}</span>
              <span style={{ ...s.cardValue, color: COLORS.eth }}>{fmtPct(summary.ethPct)}</span>
              <span style={s.cardSub}>{fmtUsd(summary.ethMarketCapUsd)}</span>
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('dominance.cardUsdt')}</span>
              <span style={{ ...s.cardValue, color: COLORS.usdt }}>{fmtPct(summary.usdtPct)}</span>
              <span style={s.cardSub}>{fmtUsd(summary.usdtMarketCapUsd)}</span>
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('dominance.cardOtherStable')}</span>
              <span style={{ ...s.cardValue, color: COLORS.otherStable }}>{fmtPct(summary.otherStablecoinsPct)}</span>
              <span style={s.cardSub}>{fmtUsd(summary.otherStablecoinsMarketCapUsd)}</span>
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('dominance.cardAlt')}</span>
              <span style={{ ...s.cardValue, color: COLORS.alt }}>{fmtPct(summary.altcoinsPct)}</span>
              <span style={s.cardSub}>{fmtUsd(summary.altcoinsMarketCapUsd)}</span>
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('dominance.cardTotalCap')}</span>
              <span style={s.cardValue}>{fmtUsd(summary.totalMarketCapUsd)}</span>
            </div>
          </div>

          {pctRows.length > 0 && (
            <div style={s.chartCard}>
              <div style={s.chartTitle}>{t('dominance.chartDominanceTitle')}</div>
              <StackedAreaChart rows={pctRows} keys={['btc', 'eth', 'usdt', 'otherStable', 'alt']} colors={COLORS} labels={pctLabels} valueFmt={fmtPct} height={240} />
              <Legend labels={pctLabels} />
            </div>
          )}

          {totalRows.length > 1 && (
            <div style={s.chartCard}>
              <div style={s.chartHead}>
                <span style={s.chartTitle}>{t('dominance.chartTotalCapTitle')}</span>
                <span style={s.chartHeadRight}>{fmtUsd(summary.totalMarketCapUsd)} <ChangeTag rows={totalRows} /></span>
              </div>
              <LineAreaChart rows={totalRows} color="var(--accent)" valueFmt={fmtUsd} height={220} />
            </div>
          )}

          {capRows.length > 1 && (
            <div style={s.chartCard}>
              <div style={s.chartTitle}>{t('dominance.chartCategoryCapTitle')}</div>
              <div style={{ ...s.miniGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                {Object.keys(COLORS).map((k) => {
                  const rows = categoryRows(k);
                  const last = [...rows].reverse().find((r) => Number.isFinite(r.v));
                  return (
                    <div key={k} style={s.miniCard}>
                      <div style={s.chartHead}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS[k] }}>{pctLabels[k]}</span>
                        <span style={s.chartHeadRight}>{fmtUsd(last?.v)} <ChangeTag rows={rows} /></span>
                      </div>
                      <LineAreaChart rows={rows} color={COLORS[k]} valueFmt={fmtUsd} height={140} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {capRows.length > 0 && (
            <div style={s.chartCard}>
              <div style={s.chartTitle}>{t('dominance.chartMarketCapTitle')}</div>
              <StackedAreaChart rows={capRows} keys={['btc', 'eth', 'usdt', 'otherStable', 'alt']} colors={COLORS} labels={pctLabels} valueFmt={fmtUsd} height={240} />
              <Legend labels={pctLabels} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Legend({ labels }) {
  return (
    <div style={s.legend}>
      {Object.entries(COLORS).map(([k, c]) => (
        <span key={k} style={{ color: c, fontWeight: 700 }}>■ {labels[k]}</span>
      ))}
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  cards: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 },
  card: { flex: '1 1 180px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 },
  cardLabel: { fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontSize: 18, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' },
  cardSub: { fontSize: 11, color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' },

  chartCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', marginBottom: 18 },
  chartTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 },
  legend: { display: 'flex', gap: 16, fontSize: 11.5, marginTop: 8, flexWrap: 'wrap' },
  chartHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 8 },
  chartHeadRight: { fontSize: 12, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', display: 'inline-flex', gap: 6, alignItems: 'baseline' },
  miniGrid: { display: 'grid', gap: 12 },
  miniCard: { border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' },
};
